/**
 * NFL teams and the team-theming engine.
 *
 * The entire interface re-themes to the user's chosen team. Eight tokens are
 * derived from the team's official primary and secondary colours, following the
 * rules in the design handoff:
 *
 *   fill    the team's official primary
 *   ink     whichever of white / near-black clears 4.5:1 against fill
 *   bg      desaturated near-black carrying a trace of the team hue
 *   surf    card / sheet surface
 *   line    borders, dividers, track fills
 *   fg      primary text
 *   dim     secondary text — must clear 4.5:1 on bg, valid ONLY on bg/surf
 *   bright  accent — must clear 4.5:1 on bg
 *
 * The four teams implemented by hand in the prototype (SF, PHI, PIT, DAL) are
 * pinned to their exact hand-tuned values; the other 28 are derived. That keeps
 * the reference screens pixel-identical while still supporting all 32.
 */

export interface Theme {
  bg: string
  surf: string
  line: string
  fg: string
  dim: string
  fill: string
  ink: string
  bright: string
}

export interface Team {
  id: string // ESPN team id
  abbr: string
  location: string
  nickname: string
  conference: 'AFC' | 'NFC'
  division: 'East' | 'North' | 'South' | 'West'
  primary: string
  secondary: string
}

export const TEAMS: Team[] = [
  { id: '22', abbr: 'ARI', location: 'Arizona',       nickname: 'Cardinals',  conference: 'NFC', division: 'West',  primary: '#97233F', secondary: '#FFB612' },
  { id: '1',  abbr: 'ATL', location: 'Atlanta',       nickname: 'Falcons',    conference: 'NFC', division: 'South', primary: '#A71930', secondary: '#A5ACAF' },
  { id: '33', abbr: 'BAL', location: 'Baltimore',     nickname: 'Ravens',     conference: 'AFC', division: 'North', primary: '#241773', secondary: '#9E7C0C' },
  { id: '2',  abbr: 'BUF', location: 'Buffalo',       nickname: 'Bills',      conference: 'AFC', division: 'East',  primary: '#00338D', secondary: '#C60C30' },
  { id: '29', abbr: 'CAR', location: 'Carolina',      nickname: 'Panthers',   conference: 'NFC', division: 'South', primary: '#0085CA', secondary: '#BFC0BF' },
  { id: '3',  abbr: 'CHI', location: 'Chicago',       nickname: 'Bears',      conference: 'NFC', division: 'North', primary: '#0B162A', secondary: '#C83803' },
  { id: '4',  abbr: 'CIN', location: 'Cincinnati',    nickname: 'Bengals',    conference: 'AFC', division: 'North', primary: '#FB4F14', secondary: '#101820' },
  { id: '5',  abbr: 'CLE', location: 'Cleveland',     nickname: 'Browns',     conference: 'AFC', division: 'North', primary: '#311D00', secondary: '#FF3C00' },
  { id: '6',  abbr: 'DAL', location: 'Dallas',        nickname: 'Cowboys',    conference: 'NFC', division: 'East',  primary: '#003594', secondary: '#869397' },
  { id: '7',  abbr: 'DEN', location: 'Denver',        nickname: 'Broncos',    conference: 'AFC', division: 'West',  primary: '#FB4F14', secondary: '#002244' },
  { id: '8',  abbr: 'DET', location: 'Detroit',       nickname: 'Lions',      conference: 'NFC', division: 'North', primary: '#0076B6', secondary: '#B0B7BC' },
  { id: '9',  abbr: 'GB',  location: 'Green Bay',     nickname: 'Packers',    conference: 'NFC', division: 'North', primary: '#203731', secondary: '#FFB612' },
  { id: '34', abbr: 'HOU', location: 'Houston',       nickname: 'Texans',     conference: 'AFC', division: 'South', primary: '#03202F', secondary: '#A71930' },
  { id: '11', abbr: 'IND', location: 'Indianapolis',  nickname: 'Colts',      conference: 'AFC', division: 'South', primary: '#002C5F', secondary: '#A2AAAD' },
  { id: '30', abbr: 'JAX', location: 'Jacksonville',  nickname: 'Jaguars',    conference: 'AFC', division: 'South', primary: '#006778', secondary: '#D7A22A' },
  { id: '12', abbr: 'KC',  location: 'Kansas City',   nickname: 'Chiefs',     conference: 'AFC', division: 'West',  primary: '#E31837', secondary: '#FFB81C' },
  { id: '13', abbr: 'LV',  location: 'Las Vegas',     nickname: 'Raiders',    conference: 'AFC', division: 'West',  primary: '#101820', secondary: '#A5ACAF' },
  { id: '24', abbr: 'LAC', location: 'Los Angeles',   nickname: 'Chargers',   conference: 'AFC', division: 'West',  primary: '#0080C6', secondary: '#FFC20E' },
  { id: '14', abbr: 'LAR', location: 'Los Angeles',   nickname: 'Rams',       conference: 'NFC', division: 'West',  primary: '#003594', secondary: '#FFA300' },
  { id: '15', abbr: 'MIA', location: 'Miami',         nickname: 'Dolphins',   conference: 'AFC', division: 'East',  primary: '#008E97', secondary: '#FC4C02' },
  { id: '16', abbr: 'MIN', location: 'Minnesota',     nickname: 'Vikings',    conference: 'NFC', division: 'North', primary: '#4F2683', secondary: '#FFC62F' },
  { id: '17', abbr: 'NE',  location: 'New England',   nickname: 'Patriots',   conference: 'AFC', division: 'East',  primary: '#002244', secondary: '#C60C30' },
  { id: '18', abbr: 'NO',  location: 'New Orleans',   nickname: 'Saints',     conference: 'NFC', division: 'South', primary: '#D3BC8D', secondary: '#101820' },
  { id: '19', abbr: 'NYG', location: 'New York',      nickname: 'Giants',     conference: 'NFC', division: 'East',  primary: '#0B2265', secondary: '#A71930' },
  { id: '20', abbr: 'NYJ', location: 'New York',      nickname: 'Jets',       conference: 'AFC', division: 'East',  primary: '#125740', secondary: '#C4C9CC' },
  { id: '21', abbr: 'PHI', location: 'Philadelphia',  nickname: 'Eagles',     conference: 'NFC', division: 'East',  primary: '#004C54', secondary: '#A5ACAF' },
  { id: '23', abbr: 'PIT', location: 'Pittsburgh',    nickname: 'Steelers',   conference: 'AFC', division: 'North', primary: '#FFB612', secondary: '#101820' },
  { id: '25', abbr: 'SF',  location: 'San Francisco', nickname: '49ers',      conference: 'NFC', division: 'West',  primary: '#AA0000', secondary: '#B3995D' },
  { id: '26', abbr: 'SEA', location: 'Seattle',       nickname: 'Seahawks',   conference: 'NFC', division: 'West',  primary: '#002244', secondary: '#69BE28' },
  { id: '27', abbr: 'TB',  location: 'Tampa Bay',     nickname: 'Buccaneers', conference: 'NFC', division: 'South', primary: '#D50A0A', secondary: '#FF7900' },
  { id: '10', abbr: 'TEN', location: 'Tennessee',     nickname: 'Titans',     conference: 'AFC', division: 'South', primary: '#0C2340', secondary: '#4B92DB' },
  { id: '28', abbr: 'WSH', location: 'Washington',    nickname: 'Commanders', conference: 'NFC', division: 'East',  primary: '#5A1414', secondary: '#FFB612' },
]

export const TEAMS_BY_ID: Record<string, Team> = Object.fromEntries(TEAMS.map((t) => [t.id, t]))
export const TEAMS_BY_ABBR: Record<string, Team> = Object.fromEntries(TEAMS.map((t) => [t.abbr, t]))

export const DEFAULT_TEAM_ID = '25' // SF — the prototype's default

export function teamName(teamId: string | null | undefined): string {
  const t = teamId ? TEAMS_BY_ID[teamId] : undefined
  return t ? `${t.location} ${t.nickname}` : 'Unknown'
}

export function teamAbbr(teamId: string | null | undefined): string {
  const t = teamId ? TEAMS_BY_ID[teamId] : undefined
  return t ? t.abbr : '-'
}

export function teamLogoUrl(teamId: string | null | undefined, size = 500): string | null {
  const t = teamId ? TEAMS_BY_ID[teamId] : undefined
  if (!t) return null
  return `https://a.espncdn.com/i/teamlogos/nfl/${size}/${t.abbr.toLowerCase()}.png`
}

// ── Colour math ─────────────────────────────────────────────────────────────

interface Hsl { h: number; s: number; l: number }

function clamp(n: number, lo: number, hi: number) { return Math.min(hi, Math.max(lo, n)) }

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '').trim()
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const n = parseInt(full, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (v: number) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase()
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0))
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  return { h: h * 60, s, l }
}

function hslToHex({ h, s, l }: Hsl): string {
  h = ((h % 360) + 360) % 360
  s = clamp(s, 0, 1); l = clamp(l, 0, 1)
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let rgb: [number, number, number]
  if (h < 60) rgb = [c, x, 0]
  else if (h < 120) rgb = [x, c, 0]
  else if (h < 180) rgb = [0, c, x]
  else if (h < 240) rgb = [0, x, c]
  else if (h < 300) rgb = [x, 0, c]
  else rgb = [c, 0, x]
  return rgbToHex((rgb[0] + m) * 255, (rgb[1] + m) * 255, (rgb[2] + m) * 255)
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a), lb = relativeLuminance(b)
  const hi = Math.max(la, lb), lo = Math.min(la, lb)
  return (hi + 0.05) / (lo + 0.05)
}

const NEAR_BLACK = '#101820'
const PURE_BLACK = '#000000'
const WHITE = '#FFFFFF'

/**
 * The handoff says `ink` is "whichever of white/near-black clears 4.5:1".
 * For most primaries one of them does comfortably. A handful of mid-tone
 * primaries — the Panthers' and Chargers' powder blues — clear neither:
 * white lands at ~4.0 and the near-black #101820 at ~4.3. Pure black gets
 * them to ~5.2, so it joins the candidate set and the best one wins.
 *
 * `ink` is the text drawn ON the team colour: score numerals, button labels,
 * the filled header. It has to be readable at 15px, so 4.5:1 is the bar, not
 * the large-text 3:1 allowance.
 */
function pickInk(fill: string): string {
  const candidates = [WHITE, NEAR_BLACK, PURE_BLACK]
  let best = WHITE
  let bestRatio = 0
  for (const c of candidates) {
    const r = contrastRatio(fill, c)
    if (r > bestRatio) { bestRatio = r; best = c }
  }
  return best
}

/** Raise lightness in small steps until `fg` clears `min` contrast on `bg`. */
function ensureContrastOn(bg: string, fg: string, min = 4.5): string {
  let hsl = rgbToHsl(...hexToRgb(fg))
  let out = fg
  for (let i = 0; i < 60 && contrastRatio(bg, out) < min; i++) {
    hsl = { ...hsl, l: Math.min(0.97, hsl.l + 0.015) }
    out = hslToHex(hsl)
  }
  return out
}

// ── Theme derivation ────────────────────────────────────────────────────────

/** Exact values from the design handoff for the four hand-built teams. */
const PINNED: Record<string, Theme> = {
  '25': { bg: '#100809', surf: '#1B0C0E', line: '#3A1A1D', fg: '#FFF3F1', dim: '#C7A6A3', fill: '#AA0000', ink: '#FFFFFF', bright: '#C9A96A' }, // SF
  '21': { bg: '#04100F', surf: '#0B1E1D', line: '#164240', fg: '#EAF6F3', dim: '#9CBDB6', fill: '#004C54', ink: '#FFFFFF', bright: '#6FD3C4' }, // PHI
  '23': { bg: '#0A0A0B', surf: '#17171B', line: '#2C2C33', fg: '#F6F6F1', dim: '#A9A9A3', fill: '#FFB612', ink: '#101820', bright: '#FFB612' }, // PIT
  '6':  { bg: '#060A12', surf: '#0F1626', line: '#20304C', fg: '#EFF4FC', dim: '#A3B2C9', fill: '#003594', ink: '#FFFFFF', bright: '#8FB0EC' }, // DAL
}

export function deriveTheme(team: Team): Theme {
  const pinned = PINNED[team.id]
  if (pinned) return pinned

  const p = rgbToHsl(...hexToRgb(team.primary))
  const sec = rgbToHsl(...hexToRgb(team.secondary))

  // A hue to tint the neutrals with. A fully desaturated primary (LV, CLE-ish)
  // still needs *some* hue, so borrow the secondary's when the primary has none.
  const hue = p.s > 0.08 ? p.h : sec.s > 0.08 ? sec.h : 220
  const sat = p.s > 0.08 ? p.s : sec.s > 0.08 ? sec.s * 0.7 : 0.18

  const bg   = hslToHex({ h: hue, s: Math.min(sat, 0.24), l: 0.046 })
  const surf = hslToHex({ h: hue, s: Math.min(sat, 0.36), l: 0.086 })
  const line = hslToHex({ h: hue, s: Math.min(sat, 0.42), l: 0.176 })
  const fg   = hslToHex({ h: hue, s: Math.min(sat, 0.55), l: 0.962 })
  const dim  = ensureContrastOn(bg, hslToHex({ h: hue, s: Math.min(sat, 0.22), l: 0.70 }), 4.5)

  const fill = team.primary.toUpperCase()
  const ink = pickInk(fill)

  // bright: the accent that has to survive on `bg`.
  //  - a primary that is already light can be its own accent (PIT gold)
  //  - a chromatic secondary makes the best accent (SF gold, GB gold, SEA green)
  //  - otherwise lighten the primary until it clears
  let bright: string
  if (p.l >= 0.45 && contrastRatio(bg, fill) >= 4.5) {
    bright = fill
  } else if (sec.s >= 0.25) {
    bright = ensureContrastOn(bg, hslToHex({ h: sec.h, s: Math.max(0.35, Math.min(sec.s, 0.72)), l: Math.max(sec.l, 0.58) }), 4.5)
  } else {
    bright = ensureContrastOn(bg, hslToHex({ h: hue, s: Math.max(0.35, Math.min(sat, 0.62)), l: 0.68 }), 4.5)
  }

  return { bg, surf, line, fg, dim, fill, ink, bright }
}

const themeCache = new Map<string, Theme>()

export function themeFor(teamId: string | null | undefined): Theme {
  const id = teamId && TEAMS_BY_ID[teamId] ? teamId : DEFAULT_TEAM_ID
  let t = themeCache.get(id)
  if (!t) {
    t = deriveTheme(TEAMS_BY_ID[id])
    themeCache.set(id, t)
  }
  return t
}

export function themeToCssVars(t: Theme): Record<string, string> {
  return {
    '--bg': t.bg,
    '--surf': t.surf,
    '--line': t.line,
    '--fg': t.fg,
    '--dim': t.dim,
    '--fill': t.fill,
    '--ink': t.ink,
    '--bright': t.bright,
  }
}

/**
 * A team chip drawn on a `fill` ground needs different colours than one on
 * `bg` — `dim` on a filled ground measures as low as 1.33:1. On fill we use
 * solid `ink` text with a 45%-alpha `ink` border instead. (Handoff, "Team
 * switcher".)
 */
export function chipOnFill(theme: Theme, selected: boolean) {
  return selected
    ? { background: theme.ink, color: theme.fill, border: `1px solid ${theme.ink}` }
    : { background: 'transparent', color: theme.ink, border: `1px solid ${theme.ink}73` }
}
